import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getUploadMeta, uploadReplay } from "@/api/upload";
import { useAuth } from "@/auth/AuthProvider";
import { HttpError } from "@/api/client";
import { canReviewAllFlights } from "@/auth/roles";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/format";

export function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["upload-meta"],
    queryFn: getUploadMeta,
    enabled: !!user,
  });

  const canAccessDirectory = canReviewAllFlights(user?.account_type);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await uploadReplay(file);
      navigate("/flights/upload/result", { state: { result } });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message);
      } else {
        setError("Upload failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Upload replay"
        lede="Schema validation, metadata and telemetry processing, metric calculation, and persistence of the result."
        actions={
          <div className="hero-stats">
            <article className="stat-card">
              <span>{canAccessDirectory ? "Visible flights" : "My flights"}</span>
              <strong>{data?.summary.flights ?? 0}</strong>
            </article>
            <article className="stat-card">
              <span>{canAccessDirectory ? "Visible imports" : "My imports"}</span>
              <strong>{data?.summary.imports ?? 0}</strong>
            </article>
            <article className="stat-card">
              <span>Ready to review</span>
              <strong>{data?.summary.ready_flights ?? 0}</strong>
            </article>
          </div>
        }
      />

      <div className="grid two-up">
        <article className="panel">
          <div className="panel-head">
            <h2>New import</h2>
            <p>DuckDB replay with `replay_metadata`, `entities`, and sample stream tables.</p>
          </div>
          {user ? (
            <>
              <div className="auth-inline-badge">
                <span>Owner of the new flight</span>
                <strong>{user.username}</strong>
              </div>
              {error && (
                <div className="callout danger">
                  <strong>Upload failed</strong>
                  <p>{error}</p>
                </div>
              )}
              <form className="upload-form" onSubmit={handleSubmit}>
                <label className="file-input">
                  <span>.duckdb file</span>
                  <input
                    ref={fileRef}
                    type="file"
                    name="file"
                    accept=".duckdb,application/octet-stream"
                    required
                  />
                </label>
                <button type="submit" className="button primary" disabled={submitting}>
                  {submitting ? "Importing…" : "Import replay"}
                </button>
                <p className="form-note">
                  Duplicate uploads are handled by both SHA-256 and `replay_id`. A new flight is
                  assigned to the signed-in user.
                </p>
              </form>
            </>
          ) : (
            <div className="auth-empty-state">
              <h3>Import is tied to an account</h3>
              <p>
                Sign in or create an account. Every newly uploaded replay is stored in that user's
                library.
              </p>
              <div className="hero-actions">
                <Link className="button primary" to="/login?next=/flights/upload">
                  Login
                </Link>
                <Link className="button ghost" to="/register?next=/flights/upload">
                  Register
                </Link>
              </div>
            </div>
          )}
        </article>

        <article className="panel">
          <div className="panel-head">
            <h2>{canAccessDirectory ? "Recent uploads" : "Your recent uploads"}</h2>
            <p>
              {canAccessDirectory
                ? "Global import audit available to privileged accounts."
                : "Audit trail of uploads tied to your account."}
            </p>
          </div>
          <div className="import-list">
            {data?.recent_imports.length ? (
              data.recent_imports.map((item) => (
                <div className="import-row" key={item.id}>
                  <div>
                    <strong>{item.source_filename}</strong>
                    <small>{formatDate(item.created_at)}</small>
                  </div>
                  <div className="import-meta">
                    <span className={`status-pill ${item.status}`}>{item.status}</span>
                    {item.flight_id && (
                      <Link to={`/flights/${item.flight_id}`}>Detail</Link>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">
                {user
                  ? "No uploads have been performed for this account yet."
                  : "Sign in to see recent uploads for your account."}
              </p>
            )}
          </div>
        </article>
      </div>
    </>
  );
}
